import {
  AggregationIcon,
  Button,
  FormControlSizes,
  HStack,
  IconButton,
  IconName,
  LoaderSizes,
  LoaderTalos,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  MixpanelSourceProvider,
  Text,
  Tooltip,
  Tour,
  useCallbackRef,
  useMixpanel,
} from '@talos/kyoko';
import { memo, useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { TourName } from '../../tokens/tours';
import { MarketSelectorContextProvider, useMarketSelectorContext } from './MarketSelectorContext';
import { MarketSelectorPanel } from './MarketSelectorPanel';
import { MarketSelectorSelectionsList } from './MarketSelectorSelectionsList';
import { NarrowMarketSelectorPanel } from './NarrowMarketSelectorPanel';
import { MarketSelectorWrapper } from './styles';
import { CUSTOM_VALUE } from './tokens';
import type { MarketSelectorProps } from './types';
import { useMarketSelectorPanel } from './useMarketSelectorPanel';

export const InnerMarketSelector = ({
  disabled: inputDisabled,
  panelAnchorRef,
  panelPlacement,
  panelOffset,
  forceOpen,
  placeholder = 'Select Aggregation...',
  selectionsListPlaceholder,
  size = FormControlSizes.Default,
}: MarketSelectorProps) => {
  const {
    presetsByID,
    ready: marketSelectorReady,
    preset,
    selections,
    allPossibleSelectionsMade,

    selectAll,
    unselectAll,
    narrowModeConfig,
    isOpen,
    setIsOpen,
    isNarrow,
    disableAggregations,
  } = useMarketSelectorContext();
  const mixpanel = useMixpanel();

  const close = useCallbackRef(() => setIsOpen(false));
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!isOpen || event.key !== 'Escape') {
        return;
      }

      close();
      event.preventDefault();
      event.stopPropagation();
    },
    [close, isOpen]
  );

  const defaultAnchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
    }
  }, [forceOpen, setIsOpen]);

  const dropdownProps = useMarketSelectorPanel({
    isOpen,
    close,
    panelOffset: panelOffset ?? 0,
    panelPlacement: panelPlacement ?? 'auto',
    panelAnchorRef: panelAnchorRef ?? defaultAnchorRef,
  });

  const getLabel = useCallback(
    (value: string) => {
      if (value === CUSTOM_VALUE) {
        return 'Custom';
      }

      return presetsByID?.get(value)?.label ?? value;
    },
    [presetsByID]
  );

  const combinedDisabled = inputDisabled || !marketSelectorReady;

  // Some logic here to get the value we wanna pass to our selector. If no selections are made, we go undefined.
  // If a preset is selected, use that. Else, we have some non-preset selection made, meaning that we show "custom"
  const selectorValue = selections.length === 0 ? undefined : preset ? preset : CUSTOM_VALUE;

  // Keep track of if the panel has ever been opened. If so, we will render the panel, and keep it rendered despite closing it.
  // This is for performance reasons on-mount.
  const [hasBeenOpenedOnce, setHasBeenOpenedOnce] = useState(false);
  useEffect(() => {
    setHasBeenOpenedOnce(curr => curr || dropdownProps.isOpen);
  }, [dropdownProps.isOpen]);

  if (!marketSelectorReady) {
    return <LoaderTalos size={LoaderSizes.SMALL} />;
  }

  return (
    <MarketSelectorWrapper data-testid="market-selector" ref={defaultAnchorRef} onKeyDown={handleKeyDown}>
      {/* Tour for market selector functionality */}
      <Tour tour={TourName.v253MarketSelector} />

      <HStack gap={size < FormControlSizes.Default ? 'spacing4' : 'spacing8'}>
        <Button
          disabled={combinedDisabled}
          onClick={() => setIsOpen(true)}
          justifyContent="flex-start"
          flex="1"
          size={size}
          data-testid="market-selector-main-select-button"
        >
          <HStack gap="spacing4" fontWeight="400" justifyContent="flex-start" w="100%">
            {!disableAggregations ? <AggregationIcon /> : null}
            {selectorValue ? (
              <Text>{getLabel(selectorValue)}</Text>
            ) : (
              <Text color="colorTextSubtle">{placeholder}</Text>
            )}
          </HStack>
        </Button>

        <Tooltip tooltip="Add Market">
          <IconButton
            size={size}
            icon={IconName.Plus}
            onClick={() => {
              setIsOpen(true);
              narrowModeConfig.setShowMarketSelectorBlotter(true);
            }}
            data-testid="market-selector-add-market-button"
          />
        </Tooltip>
        <Tooltip tooltip={allPossibleSelectionsMade ? 'Clear All' : 'Select All'}>
          <IconButton
            size={size}
            icon={allPossibleSelectionsMade ? IconName.ClearMultiple : IconName.CheckMultiple}
            onClick={() => {
              if (allPossibleSelectionsMade) {
                mixpanel.track(MixpanelEvent.UpdateMarketSelection, {
                  [MixpanelEventProperty.Method]: 'ClearAll',
                });
                unselectAll();
              } else {
                mixpanel.track(MixpanelEvent.UpdateMarketSelection, {
                  [MixpanelEventProperty.Method]: 'SelectAll',
                });
                selectAll();
              }
            }}
            data-action={allPossibleSelectionsMade ? 'clear' : 'select'}
            data-testid="clear-all-select-all-button"
          />
        </Tooltip>
      </HStack>

      {isNarrow ? (
        <NarrowMarketSelectorPanel dropdownProps={dropdownProps} />
      ) : (
        /* For the normal market selector (not popout related), only mount the panel after we have been opened the first time */
        hasBeenOpenedOnce && <MarketSelectorPanel dropdownProps={dropdownProps} />
      )}

      <MarketSelectorSelectionsList placeholder={selectionsListPlaceholder} />
    </MarketSelectorWrapper>
  );
};

/**
 * Render a MarketSelector. Requires props received from the useMarketSelector hook.
 *
 * This MarketSelector renders a BaseSelect with a MarketSelectorBlotter in a subsequent popup/dropdown.
 *
 * This MarketSelector component is just one of many possible view implementations of the market selector state and api.
 */
export const MarketSelector = memo((props: MarketSelectorProps) => {
  return (
    <MarketSelectorContextProvider
      isNarrow={!!props.isNarrow}
      marketSelectorAPI={props.marketSelectorAPI}
      showCredentialsOnlyToggle={props.showCredentialsOnlyToggle}
      disableAggregations={props.disableAggregations}
    >
      <MixpanelSourceProvider value={MixpanelEventSource.MarketAccountSelector}>
        <InnerMarketSelector {...props} />
      </MixpanelSourceProvider>
    </MarketSelectorContextProvider>
  );
});
